import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Alert,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import keywordService, { BlockedKeyword } from '../services/keywordService';
import petCategoriesService, { PetCategory } from '../services/petCategoriesService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const Keywords: React.FC = () => {
  const [keywords, setKeywords] = useState<BlockedKeyword[]>([]);
  const [categories, setCategories] = useState<PetCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<BlockedKeyword | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    loadKeywords();
    loadCategories();
  }, []);

  useEffect(() => {
    loadKeywords();
  }, [searchText, categoryFilter, activeFilter]);

  const loadKeywords = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (searchText) filters.search = searchText;
      if (categoryFilter !== undefined) filters.category = categoryFilter;
      if (activeFilter !== undefined) filters.isActive = activeFilter;

      const data = await keywordService.getKeywords(filters);
      setKeywords(data);
    } catch (error: any) {
      message.error(error.message || 'Failed to load keywords');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await petCategoriesService.getCategories();
      // Handle response structure - same as Categories.tsx
      const categoriesData = response.data?.categories || response.data || response || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error: any) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  };

  const handleCreate = () => {
    setEditingKeyword(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      caseSensitive: false,
      matchType: 'contains',
      errorMessage: 'Your post contains content that violates our community guidelines.',
    });
    setModalVisible(true);
  };

  const handleEdit = (keyword: BlockedKeyword) => {
    setEditingKeyword(keyword);
    form.setFieldsValue({
      keyword: keyword.keyword,
      category: keyword.category?._id || null,
      errorMessage: keyword.errorMessage,
      isActive: keyword.isActive,
      caseSensitive: keyword.caseSensitive,
      matchType: keyword.matchType,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await keywordService.deleteKeyword(id);
      message.success('Keyword deleted successfully');
      loadKeywords();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete keyword');
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await keywordService.toggleKeywordStatus(id);
      message.success('Keyword status updated');
      loadKeywords();
    } catch (error: any) {
      message.error(error.message || 'Failed to update keyword status');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingKeyword) {
        await keywordService.updateKeyword(editingKeyword._id, values);
        message.success('Keyword updated successfully');
      } else {
        await keywordService.createKeyword(values);
        message.success('Keyword created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      loadKeywords();
    } catch (error: any) {
      message.error(error.message || 'Failed to save keyword');
    }
  };

  const columns = [
    {
      title: 'Keyword',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (text: string, record: BlockedKeyword) => (
        <Space>
          <Text strong>{text}</Text>
          {record.caseSensitive && <Tag color="orange">Case Sensitive</Tag>}
          <Tag>{record.matchType}</Tag>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: BlockedKeyword['category']) => (
        category ? (
          <Tag color="blue">{category.name}</Tag>
        ) : (
          <Tag color="default">All Categories</Tag>
        )
      ),
    },
    {
      title: 'Error Message',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: (text: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {text || 'Default message'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: BlockedKeyword) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            onClick={() => handleToggleStatus(record._id)}
          >
            {record.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this keyword?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Blocked Keywords Management</Title>
      <Text type="secondary">
        Manage blocked keywords to prevent inappropriate content in posts. Keywords can be category-specific or apply to all categories.
      </Text>

      <Divider />

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search keywords..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by category"
              style={{ width: '100%' }}
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
            >
              <Option value={null}>All Categories (Global)</Option>
              {Array.isArray(categories) && categories.map((cat) => (
                <Option key={cat._id} value={cat._id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by status"
              style={{ width: '100%' }}
              value={activeFilter}
              onChange={setActiveFilter}
              allowClear
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Col>
        </Row>

        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Keyword
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadKeywords}>
            Refresh
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={keywords}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} keywords`,
          }}
        />
      </Card>

      <Modal
        title={editingKeyword ? 'Edit Keyword' : 'Add Blocked Keyword'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="keyword"
            label="Keyword"
            rules={[{ required: true, message: 'Please enter a keyword' }]}
          >
            <Input placeholder="Enter keyword to block" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            tooltip="Select a category to make this keyword category-specific, or leave empty for all categories"
          >
            <Select placeholder="Select category (optional)" allowClear>
              <Option value={null}>All Categories (Global)</Option>
              {Array.isArray(categories) && categories.map((cat) => (
                <Option key={cat._id} value={cat._id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="matchType"
                label="Match Type"
                tooltip="How the keyword should be matched in text"
              >
                <Select>
                  <Option value="contains">Contains (default)</Option>
                  <Option value="exact">Exact Match</Option>
                  <Option value="word">Whole Word</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="caseSensitive"
                label="Case Sensitive"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="errorMessage"
            label="Custom Error Message"
            tooltip="Message shown to users when this keyword is detected"
          >
            <TextArea
              rows={3}
              placeholder="Your post contains content that violates our community guidelines."
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Card style={{ marginTop: 24 }}>
        <Title level={4}>How Keyword Blocking Works</Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message="Match Types"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>Contains:</strong> Blocks if the keyword appears anywhere in the text</li>
                <li><strong>Exact Match:</strong> Blocks only if the text exactly matches the keyword</li>
                <li><strong>Whole Word:</strong> Blocks if the keyword appears as a complete word (not part of another word)</li>
              </ul>
            }
            type="info"
            showIcon
          />
          <Alert
            message="Category-Specific Keywords"
            description="Keywords can be set for specific pet categories. If a keyword has no category, it applies to all posts regardless of category."
            type="info"
            showIcon
          />
          <Alert
            message="Case Sensitivity"
            description="When enabled, the keyword matching will be case-sensitive. When disabled, 'Keyword' and 'keyword' will both be blocked."
            type="info"
            showIcon
          />
        </Space>
      </Card>
    </div>
  );
};

export default Keywords;

